"use client";
import { useState, useMemo } from "react";
import Link from "next/link";
import { capitalizeFirstLetter } from "@/util/app.util";

interface StateTableProps {
  heading: string | React.ReactElement;
  list: string[];
  country: string;
}

export const StateTable = ({ country, heading, list }: StateTableProps) => {
  const alphabets = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const [selectedLetter, setSelectedLetter] = useState<string>("");

  // Sort list once before rendering
  const sortedList = useMemo(() => list.sort((a, b) => a.localeCompare(b)), [
    list,
  ]);

  // Memoized filtered list to prevent unnecessary recalculations
  const filteredList = useMemo(
    () =>
      selectedLetter
        ? sortedList.filter((item) => item.startsWith(selectedLetter, 0))
        : sortedList,
    [selectedLetter, sortedList]
  );

  return (
    <div className="flex flex-col items-start gap-10 w-full">
      <div className="w-full flex flex-col gap-y-4">
        <h2 className="text-neutral-light-500 font-poppins">{heading}</h2>

        {/* Alphabet Filter */}
        <div className="flex flex-col md:flex-row w-full gap-y-4 md:gap-x-4 mt-4 md:items-center">
          <p className="text-sm lg:text-lg text-neutral-light-900 font-poppinsMedium font-medium whitespace-nowrap">
            Filter by letter:
          </p>
          <div className="flex gap-2 flex-wrap">
            {alphabets.map((letter) => (
              <button
                key={letter}
                onClick={() => setSelectedLetter(letter)}
                className={`px-2 py-1 text-sm lg:text-base underline decoration-neutral-dark-800 underline-offset-4 ${
                  selectedLetter === letter
                    ? "text-neutral-100 font-bold decoration-neutral-light-500"
                    : "text-neutral-light-400 lg:text-neutral-light-700"
                }`}
              >
                {letter}
              </button>
            ))}
            {selectedLetter.length > 0 && (
              <button
                onClick={() => setSelectedLetter("")}
                className={`px-2 py-1 text-sm underline underline-offset-4 hover:text-neutral-light-500 text-neutral-200`}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* State List */}
      <ul className="grid grid-cols-2 xs:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-4 w-[99%] lg:w-full text-neutral-light-200 max-h-[30rem] lg:max-h-full lg:h-full xs:h-full overflow-auto pb-2">
        {filteredList.length > 0 ? (
          filteredList.map((item) => (
            <li
              key={item}
              className="underline hover:text-neutral-light-100 decoration-1 decoration-neutral-dark-800 hover:decoration-neutral-dark-600 underline-offset-8 lg:no-underline text-wrap"
            >
              <Link
                href={`/${country == "usa" ? "us" : "uk"}/${item
                  .replaceAll(" ", "-")
                  .toLowerCase()}`}
                className="text-sm lg:text-base text-neutral-light-200 hover:text-neutral-light-100"
              >
                {capitalizeFirstLetter(item)}
              </Link>
            </li>
          ))
        ) : (
          <p className="text-sm lg:text-base text-neutral-700">
            No results found
          </p>
        )}
      </ul>
    </div>
  );
};
